import { CompensationMethod } from "@redotech/redo-return-core-common/compensation-method";
import { defaultCompensationMethods } from "@redotech/redo-return-core-common/dropoff-provider";
import {
  excessExchangeValueOptions,
  OutOfStockAdvanced,
} from "@redotech/redo-return-core-common/exchange-settings";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import {
  BLUE_YONDER_PROVIDER,
  dropoffProviderType,
  HAPPY_RETURNS_PROVIDER,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { AgentReviewAction, AgentReviewRiskLevel } from "../agent-review";
import { mergeDropoffAutoProcessMethods } from "../dropoff-settings-merge";
import {
  dropoffSettingsFromStored,
  type StoredDropoff,
} from "../returns-locations-settings";
import type {
  OperationsSettingsPatch,
  ReturnsOperationsSettingsSource,
} from "./operations-settings-source";

export const processingSettingsShape = {
  processing: z.object({
    compensationEnabled: z.boolean(),
    restockingEnabled: z.boolean(),
    reviewAfterWarehouseProcessing: z.boolean(),
    checkForTryNowTags: z.boolean(),
  }),
  general: z.object({
    waiveRefundStoreCreditBelowAmount: z.number().min(0),
    addReturnTagToOriginalOrder: z.boolean(),
    defaultReimburseOriginalShipping: z.boolean(),
    exposeExternalId: z.boolean(),
  }),
  fulfillment: z.object({
    ignoreInventory: z.boolean(),
    excessExchangeValue: z.enum(excessExchangeValueOptions),
    outOfStockAdvanced: createEnum(OutOfStockAdvanced),
  }),
  greenReturns: z.object({
    autoProcessGreenReturnTypes: z.array(createEnum(ReturnTypeEnum)),
    autoProcessMerchantCreatedGreenReturnTypes: z.array(
      createEnum(ReturnTypeEnum),
    ),
  }),
  /** Keyed by drop-off provider; shares `returns.dropoffs` with the Locations tab. */
  dropoffAutoProcessing: z.object({
    [VIRTUAL_PROVIDER]: z.array(createEnum(CompensationMethod)),
    [RETURN_BEAR_PROVIDER]: z.array(createEnum(CompensationMethod)),
    [BLUE_YONDER_PROVIDER]: z.array(createEnum(CompensationMethod)),
    [HAPPY_RETURNS_PROVIDER]: z.array(createEnum(CompensationMethod)),
  }),
  predefinedCategories: z.object({
    enabled: z.boolean(),
    categories: z.array(z.object({ name: z.string(), code: z.string() })),
  }),
  /** Only shown to teams Redo turned agent review on for. */
  agentReview: z.object({
    riskConfigs: z.array(
      z.object({
        riskLevel: createEnum(AgentReviewRiskLevel),
        action: createEnum(AgentReviewAction),
      }),
    ),
  }),
};

export type ProcessingSettings = z.infer<
  z.ZodObject<typeof processingSettingsShape>
>;

/** A stored entry without the array reads as none; no entry at all reads as every method. */
function dropoffAutoProcessingFromStored(
  stored: StoredDropoff[] | null | undefined,
): ProcessingSettings["dropoffAutoProcessing"] {
  const methodsFor = (provider: (typeof dropoffProviderType)[number]) => {
    const existing = stored?.find((dropoff) => dropoff.provider === provider);
    return existing
      ? (existing.eligibleAutoProcessCompensationMethods ?? [])
      : defaultCompensationMethods(provider);
  };
  return {
    [VIRTUAL_PROVIDER]: methodsFor(VIRTUAL_PROVIDER),
    [RETURN_BEAR_PROVIDER]: methodsFor(RETURN_BEAR_PROVIDER),
    [BLUE_YONDER_PROVIDER]: methodsFor(BLUE_YONDER_PROVIDER),
    [HAPPY_RETURNS_PROVIDER]: methodsFor(HAPPY_RETURNS_PROVIDER),
  };
}

export function processingSettingsFromTeam({
  settings,
}: ReturnsOperationsSettingsSource): ProcessingSettings {
  const { exchanges, returns } = settings;
  const compensationEnabled =
    returns?.advancedProcessing?.compensationEnabled ?? false;
  return {
    processing: {
      compensationEnabled,
      restockingEnabled:
        compensationEnabled &&
        (returns?.advancedProcessing?.restockingEnabled ?? false),
      reviewAfterWarehouseProcessing:
        returns?.reviewAfterWarehouseProcessing ?? false,
      checkForTryNowTags: returns?.checkForTryNowTags ?? false,
    },
    general: {
      waiveRefundStoreCreditBelowAmount:
        returns?.waiveRefundStoreCreditBelowAmount ?? 0,
      addReturnTagToOriginalOrder:
        returns?.addReturnTagToOriginalOrder ?? false,
      defaultReimburseOriginalShipping:
        returns?.defaultReimburseOriginalShipping ?? false,
      exposeExternalId: settings.returnsUI?.exposeExternalId ?? false,
    },
    fulfillment: {
      ignoreInventory: settings.inventory.ignoreInventory ?? false,
      excessExchangeValue: exchanges.excessExchangeValue,
      outOfStockAdvanced: exchanges.outOfStockAdvanced,
    },
    greenReturns: {
      autoProcessGreenReturnTypes:
        settings.autoProcessGreenReturnTypes ??
        (settings.autoProcessGreenReturns ? Object.values(ReturnTypeEnum) : []),
      autoProcessMerchantCreatedGreenReturnTypes:
        settings.autoProcessMerchantCreatedGreenReturnTypes ?? [],
    },
    dropoffAutoProcessing: dropoffAutoProcessingFromStored(returns?.dropoffs),
    predefinedCategories: {
      enabled: returns?.aiReturnReasons?.usePredefinedCategories ?? false,
      categories:
        returns?.aiReturnReasons?.predefinedCategories?.map(
          ({ name, code }) => ({ name, code }),
        ) ?? [],
    },
    agentReview: {
      riskConfigs: Object.values(AgentReviewRiskLevel).map((riskLevel) => ({
        riskLevel,
        action:
          returns?.agentReviewRiskConfigs?.find(
            (config) => config.riskLevel === riskLevel,
          )?.action ?? AgentReviewAction.NONE,
      })),
    },
  };
}

export function processingTeamPatch(
  { settings }: ReturnsOperationsSettingsSource,
  next: ProcessingSettings,
): OperationsSettingsPatch {
  return {
    set: {
      "returns.waiveRefundStoreCreditBelowAmount":
        next.general.waiveRefundStoreCreditBelowAmount,
      "returns.addReturnTagToOriginalOrder":
        next.general.addReturnTagToOriginalOrder,
      "returns.defaultReimburseOriginalShipping":
        next.general.defaultReimburseOriginalShipping,
      "returnsUI.exposeExternalId": next.general.exposeExternalId,
      "inventory.ignoreInventory": next.fulfillment.ignoreInventory,
      "exchanges.excessExchangeValue": next.fulfillment.excessExchangeValue,
      "exchanges.outOfStockAdvanced": next.fulfillment.outOfStockAdvanced,
      "returns.advancedProcessing.compensationEnabled":
        next.processing.compensationEnabled,
      "returns.advancedProcessing.restockingEnabled":
        next.processing.compensationEnabled &&
        next.processing.restockingEnabled,
      "returns.reviewAfterWarehouseProcessing":
        next.processing.reviewAfterWarehouseProcessing,
      "returns.checkForTryNowTags": next.processing.checkForTryNowTags,
      autoProcessGreenReturnTypes:
        next.greenReturns.autoProcessGreenReturnTypes,
      autoProcessMerchantCreatedGreenReturnTypes:
        next.greenReturns.autoProcessMerchantCreatedGreenReturnTypes,
      "returns.dropoffs": mergeDropoffAutoProcessMethods(
        settings.returns?.dropoffs?.map(dropoffSettingsFromStored),
        dropoffProviderType.map((provider) => ({
          provider,
          methods: next.dropoffAutoProcessing[provider],
        })),
      ),
      "returns.aiReturnReasons.usePredefinedCategories":
        next.predefinedCategories.enabled,
      // Incomplete rows are dropped, as the legacy page did on save.
      "returns.aiReturnReasons.predefinedCategories":
        next.predefinedCategories.categories
          .map(({ name, code }) => ({ name: name.trim(), code: code.trim() }))
          .filter(({ name, code }) => name && code),
      // "No action" levels are left out, as the legacy page stored them.
      "returns.agentReviewRiskConfigs": next.agentReview.riskConfigs.filter(
        (config) => config.action !== AgentReviewAction.NONE,
      ),
    },
    unset: [],
  };
}
